import { ProductService } from "@sdk-point/talisis";
import { addProductsToCart } from "./cartHelper";
import { getNameMembership, getPeriodMembership } from "./checkoutHelpers";
import { eventTracker } from "../components/EventTracker/EventTracker";
import { log } from "./console";

const ProductServiceSDK = new ProductService(
    process.env.REACT_APP_ENV,
    process.env.REACT_APP_X_API_KEY,
    ""
);

/**
 * Maneja el evento de tracking para recompra de membresía
 * @param {Object} product - Información del producto
 * @param {Object} user - Información del usuario
 */
export const handleMembershipRepurchaseEventTrack = (product, user) => {
    try {
        const eventData = {};
        eventData['nombreArticulo'] = product?.name;
        eventData['precioArticulo'] = product?.list_price;
        eventData['articuloType'] = "membresías";
        eventData['userId'] = user?.matricula || user?.person_id || "";
        eventData['subscriptionType'] = getNameMembership(product?.name);
        eventData['periodoPago'] = getPeriodMembership(product?.sku);
        eventTracker('agregar_membresia', eventData, ['mixpanel', 'hubspot', 'google']);
    } catch (e) {
        log("event tracker error", e);
    }
};

/**
 * Maneja el evento de tracking para membresía con add-on
 * @param {Object} standalone - Información del producto standalone
 * @param {Object} userAddOn - Información del add-on del usuario
 * @param {Object} user - Información del usuario
 */
export const handleMembershipAddonEventTrack = (standalone, userAddOn, user) => {
    try {
        const eventData = {};
        eventData['nombreArticulo'] = userAddOn?.name;
        eventData['precioArticulo'] = userAddOn?.list_price + standalone?.list_price;
        eventData['userId'] = user?.matricula || user?.person_id || "";
        eventData['articuloType'] = "addOn";
        eventData['subscriptionType'] = getNameMembership(standalone?.name);
        eventTracker('agregar_membresia_addon', eventData, ['mixpanel', 'hubspot', 'google']);
    } catch (e) {
        log("event agregar_membresia_addon error", e);
    }
};

/**
 * Intenta realizar el pago manual de la membresía agregando productos al carrito
 * @param {Object} params - Parámetros de la función
 * @param {Object} params.userMembership - Membresía actual del usuario
 * @param {Object} params.userAddOn - Add-on del usuario (opcional)
 * @param {Object} params.cart - Carrito actual
 * @param {Object} params.user - Información del usuario
 * @param {Function} params.dispatchCart - Función dispatch para actualizar el carrito en Redux
 * @param {Function} params.applyCart - Action creator para aplicar carrito
 * @param {Function} params.applyCartProducts - Action creator para aplicar productos del carrito
 * @param {Function} params.history - Objeto history de react-router para navegación
 * @param {string} params.cartPath - Ruta del carrito (ej: '/cart')
 * @param {Function} params.setLoadingRepurchase - Función para controlar estado de carga (opcional)
 * @returns {Promise<void>}
 */
export const manuallyAttemptMembershipPayment = async ({
    userMembership,
    userAddOn,
    cart,
    user,
    dispatchCart,
    applyCart,
    applyCartProducts,
    history,
    cartPath,
    setLoadingRepurchase
}) => {
    try {
        if (setLoadingRepurchase) setLoadingRepurchase(true);

        // Obtener información del producto
        const productInfo = await ProductServiceSDK.getProductById(userMembership?.product_id);

        // Preparar los productos para la recompra manual
        const products = [];
        products.push({
            "product_id": productInfo.id,
            "product_name": productInfo.name,
            "product_image_url": productInfo?.image_url,
            "product_description": productInfo?.description,
            "product_type_id": productInfo?.product_type_id,
            "quantity": 1,
            "product_price_id": productInfo?.product_price_id,
            "unit_price": productInfo?.list_price,
            "discount": 0
        });

        // Agregar el add-on a la recompra si el usuario ya contaba con uno
        if (userAddOn) {
            products.push({
                "product_id": userAddOn.id,
                "product_name": userAddOn.name,
                "product_image_url": userAddOn.image_url,
                "product_description": userAddOn.description,
                "product_type_id": userAddOn.product_type_id,
                "quantity": 1,
                "product_price_id": userAddOn.product_price_id,
                "unit_price": userAddOn.list_price,
                "discount": 0
            });
        }

        // Agregar productos al carrito
        const response = await addProductsToCart({
            cart_id: cart?.id,
            products: [...products]
        });

        // Event Tracker v2
        if (userAddOn) {
            handleMembershipAddonEventTrack(productInfo, userAddOn, user);
        } else {
            handleMembershipRepurchaseEventTrack(productInfo, user);
        }

        // Actualizar estado reducer del carrito
        dispatchCart(applyCart(response));
        dispatchCart(applyCartProducts(response.cart_details));

        if (setLoadingRepurchase) setLoadingRepurchase(false);

        // Redireccionar al carrito para ver el detalle
        history.push(cartPath);
    } catch (e) {
        log("error", e);
        if (setLoadingRepurchase) setLoadingRepurchase(false);
    }
};
